import { CasesService } from '@/@types/generated'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import React, { PropsWithChildren, useEffect } from 'react'
import { toast } from 'sonner'
import { OpenCaseStatuses } from '@/modules/cases/lib/case-status'
import { Link } from 'react-router'
import { buildPath } from '@/routes'

interface MotorCarrierOpenCasesDialogProps {
  certificateNumber: string
  isReRegistration: boolean
}

// Dialog for asking whether to add a vehicle to the
// current registration year, as well as the renewal
export default function MotorCarrierOpenCasesDialog({
  certificateNumber,
  isReRegistration,
  children,
}: PropsWithChildren<MotorCarrierOpenCasesDialogProps>) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [openCases, setOpenCases] = React.useState(0)

  // Check if the carrier has any open cases linked to its certificate number
  useEffect(() => {
    const fetchOpenCases = async () => {
      try {
        // TODO: Replace this with cases-provider to centralize this list of arguments
        const { total } = await CasesService.listCases(
          undefined, // query
          OpenCaseStatuses,
          undefined, // investigators
          undefined, // type primary
          undefined, // operation type
          undefined, // region
          undefined, // investigation status
          undefined, // carrier status
          undefined, // notice of violation issued
          certificateNumber, // certificate number
          undefined, // notice type
          undefined, // nodd letter sent
          undefined, // final order sent
          undefined, // attorney(s)
          undefined, // nodd letter printed
          undefined, // final order letter printed
          undefined, // nodd due date passed
          undefined, // created at
          1 // limit - all we need is the total count
        )

        setOpenCases(total)
        setIsOpen(total > 0)
      } catch (error) {
        toast.error('Failed to check for open cases.')
      }
    }

    fetchOpenCases()
  }, [certificateNumber])

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="flex flex-col md:max-w-lg" variant="warning">
        <DialogHeader>
          <DialogTitle>Open Cases Found</DialogTitle>
          <DialogDescription className="mb-2">
            There
            {openCases === 1 ? (
              // Singular
              <>
                {' is '}
                <span className="font-semibold underline">{openCases}</span>
                {' case '}
              </>
            ) : (
              // Plural
              <>
                {' are '}
                <span className="font-semibold underline">{openCases}</span>
                {' cases '}
              </>
            )}
            associated with your certificate under investigation by TxDMV.
          </DialogDescription>
          <DialogDescription className="mb-2">
            You may still proceed with the{' '}
            {isReRegistration ? 're-registration' : 'renewal'}, but it may
            affect your certificate status. We recommend resolving any open
            cases before {isReRegistration ? 're-registering' : 'renewing'} your
            certificate.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="link" asChild>
            <Link
              to={{
                pathname: buildPath('/cases'),
                search: `?query=${encodeURIComponent(certificateNumber)}&status=${OpenCaseStatuses.join(',')}`,
              }}
            >
              View Open Cases
            </Link>
          </Button>
          <Button onClick={() => setIsOpen(false)} variant="warning">
            I understand, proceed with{' '}
            {isReRegistration ? 're-registration' : 'renewal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
